import {
  Box,
  Container,
  Grid,
  Typography,
  useTheme,
} from "@mui/material";
import { memo, useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import AirHeardLeftIMG from "../../assets/images/AudiogramImages/AirHeardLeft.svg";
import AirHeardRightIMG from "../../assets/images/AudiogramImages/AirHeardRight.svg";
import AirNotHeardLeftIMG from "../../assets/images/AudiogramImages/AirNotHeardLeft.svg";
import AirNotHeardRightIMG from "../../assets/images/AudiogramImages/AirNotHeardRight.svg";
import BoneHeardLeftIMG from "../../assets/images/AudiogramImages/BoneHeardLeft.svg";
import BoneHeardRightIMG from "../../assets/images/AudiogramImages/BoneHeardRight.svg";
import BoneNotHeardLeftIMG from "../../assets/images/AudiogramImages/BoneNotHeardLeft.svg";
import BoneNotHeardRightIMG from "../../assets/images/AudiogramImages/BoneNotHeardRight.svg";

const backgroundZonesPlugin = {
  id: "backgroundZones",
  beforeDraw: (chart) => {
    const {
      ctx,
      chartArea: { left, right, top, bottom },
      scales,
    } = chart;

    const zones = [
      // { label: "", color: "#DFFFD8", minY: -10, maxY: 25 },
      { label: "Mild", color: "#DFFFD8", minY: 25, maxY: 40 },
      { label: "Moderate", color: "#FFEBCC", minY: 40, maxY: 55 },
      { label: "Moderate Severe", color: "#b8bece", minY: 55, maxY: 70 },
      { label: "Severe", color: "#FFE0E2	", minY: 70, maxY: 90 },
      { label: "Profound", color: "#c1b2c5	", minY: 90, maxY: 120 },
    ];

    zones.forEach((zone) => {
      const yStart = scales.y.getPixelForValue(zone.minY);
      const yEnd = scales.y.getPixelForValue(zone.maxY);
      const height = yEnd - yStart;

      // Draw colored background
      ctx.save();
      ctx.fillStyle = zone.color;
      ctx.fillRect(left, yStart, right - left, height);

      // Draw text
      ctx.fillStyle = "#000";
      ctx.font = "12px Arial";
      ctx.fillText(
        zone.label,
        right - ctx.measureText(zone.label).width - 10,
        yStart + height / 2
      );

      ctx.restore();
    });
  },
};

const dottedGridPlugin = {
  id: "dottedGridPlugin",
  beforeDatasetsDraw(chart, args, pluginOptions) {
    const { ctx, chartArea, scales } = chart;
    if (!chartArea) return;

    const xScale = scales.x;
    const yScale = scales.y;
    const xTargets = (pluginOptions && pluginOptions.xTargets) || ["750", "1.5k", "3k", "6k"];

    ctx.save();

    ctx.setLineDash([4, 4]);
    ctx.lineWidth = 1;
    ctx.strokeStyle = (pluginOptions && pluginOptions.xColor) || "#999";

    xTargets.forEach((lbl) => {
      const idx = xScale.ticks.findIndex((t) => t.label === lbl);
      if (idx !== -1) {
        const x = xScale.getPixelForTick(idx);
        ctx.beginPath();
        ctx.moveTo(x, chartArea.top);
        ctx.lineTo(x, chartArea.bottom);
        ctx.stroke();
      }
    });

    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = (pluginOptions && pluginOptions.yColor) || "#bbb";

    ctx.restore();
  },
};

const ChartComponent = ({
  rightEar,
  heardAC,
  heardBC,
  n_heardAC,
  n_heardBC,
}) => {
  const theme = useTheme();
  const labels = ["125", "250", "500", "750", "1k", "1.5k", "2k", "3k", "4k", "6k", "8k"];

  const [images, setImages] = useState(null);

  useEffect(() => {
    let isMounted = true;
    let loaded = 0;
    const imgObjs = {
      BoneHeardIMG: new Image(),
      BoneNotHeardIMG: new Image(),
      AirHeardIMG: new Image(),
      AirNotHeardIMG: new Image(),
    };

    const handleLoad = () => {
      loaded += 1;
      if (loaded === 4 && isMounted) {
        setImages(imgObjs);
      }
    };

    imgObjs.BoneHeardIMG.onload = handleLoad;
    imgObjs.BoneHeardIMG.onerror = handleLoad;
    imgObjs.BoneHeardIMG.src = rightEar ? BoneHeardRightIMG : BoneHeardLeftIMG;
    imgObjs.BoneHeardIMG.height = 12;
    imgObjs.BoneHeardIMG.width = 12;

    imgObjs.BoneNotHeardIMG.onload = handleLoad;
    imgObjs.BoneNotHeardIMG.onerror = handleLoad;
    imgObjs.BoneNotHeardIMG.src = rightEar ? BoneNotHeardRightIMG : BoneNotHeardLeftIMG;
    imgObjs.BoneNotHeardIMG.height = 22;
    imgObjs.BoneNotHeardIMG.width = 22;

    imgObjs.AirHeardIMG.onload = handleLoad;
    imgObjs.AirHeardIMG.onerror = handleLoad;
    imgObjs.AirHeardIMG.src = rightEar ? AirHeardRightIMG : AirHeardLeftIMG;
    imgObjs.AirHeardIMG.height = 12;
    imgObjs.AirHeardIMG.width = 12;

    imgObjs.AirNotHeardIMG.onload = handleLoad;
    imgObjs.AirNotHeardIMG.onerror = handleLoad;
    imgObjs.AirNotHeardIMG.src = rightEar ? AirNotHeardRightIMG : AirNotHeardLeftIMG;
    imgObjs.AirNotHeardIMG.height = 16;
    imgObjs.AirNotHeardIMG.width = 16;

    return () => {
      isMounted = false;
    };
  }, [rightEar]);

  if (!images) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '300px' }}></Box>;
  }

  const { BoneHeardIMG, BoneNotHeardIMG, AirHeardIMG, AirNotHeardIMG } = images;

  const filterPoints = (labels, data) => {
    const points = [];
    labels.forEach((label, idx) => {
      if (data && data[idx] != null && data[idx] !== undefined) {
        points.push({ x: label, y: Number(data[idx]) });
      }
    });
    return points;
  };

  const splitConsecutiveSegments = (labels, points, notHeardLabels) => {
    const segments = [];
    let currentSeg = [];
    points.forEach((pt, idx) => {
      const labelIdx = labels.indexOf(pt.x);
      if (
        currentSeg.length > 0 &&
        labels
          .slice(
            labels.indexOf(currentSeg[currentSeg.length - 1].x) + 1,
            labelIdx
          )
          .some(l => notHeardLabels.has(l))
      ) {
        segments.push([...currentSeg]);
        currentSeg = [pt];
      } else {
        currentSeg.push(pt);
      }
    });
    if (currentSeg.length) segments.push(currentSeg);
    return segments;
  };

  const heardACPoints = filterPoints(labels, heardAC);
  const nHeardACPoints = filterPoints(labels, n_heardAC);
  const heardBCPoints = filterPoints(labels, heardBC);
  const nHeardBCPoints = filterPoints(labels, n_heardBC);
  const nHeardACLabels = new Set(nHeardACPoints.map(p => p.x));
  const nHeardBCLabels = new Set(nHeardBCPoints.map(p => p.x));

  const heardACSegments = splitConsecutiveSegments(labels, heardACPoints, nHeardACLabels);
  const heardBCSegments = splitConsecutiveSegments(labels, heardBCPoints, nHeardBCLabels);

  const datasets = [];
  heardACSegments.forEach(seg => {
    datasets.push({
      label: "Fitting Range AC heard",
      data: seg,
      parsing: false,
      pointBackgroundColor: "white",
      pointStyle: AirHeardIMG,
      borderColor: rightEar ? "#E62323" : "#1265A1",
      radius: rightEar ? 4 : 7,
      showLine: true,
    });
  });

  nHeardACPoints.forEach(pt => {
    datasets.push({
      label: "Fitting Range AC Not heard",
      data: [pt],
      parsing: false,
      pointBackgroundColor: "white",
      pointStyle: AirNotHeardIMG,
      borderColor: rightEar ? "#E62323" : "#1265A1",
      radius: rightEar ? 4 : 7,
      showLine: false,
    });
  });
  // For heard BC, join only consecutive segments
  heardBCSegments.forEach(seg => {
    datasets.push({
      label: "Fitting Range BC heard",
      data: seg,
      parsing: false,
      pointBackgroundColor: "white",
      pointStyle: BoneHeardIMG,
      borderColor: rightEar ? "#E62323" : "#1265A1",
      radius: rightEar ? 4 : 7,
      showLine: true,
    });
  });
  // For not heard BC, show each as individual point (no line)
  nHeardBCPoints.forEach(pt => {
    datasets.push({
      label: "Fitting Range BC Not heard",
      data: [pt],
      parsing: false,
      pointBackgroundColor: "white",
      pointStyle: BoneNotHeardIMG,
      borderColor: rightEar ? "#E62323" : "#1265A1",
      radius: rightEar ? 4 : 7,
      showLine: false,
    });
  });

  const dataFields = {
    labels,
    datasets,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: {
          display: true,
          lineWidth: 1,
          color: (ctx) => {
            const lbl = ctx.tick && ctx.tick.label;
            if (["750", "1.5k", "3k", "6k"].includes(lbl)) return "transparent";
            return "#ccc";
          },
        },
        ticks: {
          font: {
            size: 10,
            color: "#666666",
          },
        },
      },
      y: {
        min: -10,
        max: 120,
        reverse: true,
        grid: {
          display: true,
          lineWidth: 1,
          // color: (ctx) => (ctx.tick.value % 20 !== 0 ? "transparent" : "#ccc"),
        },
        ticks: {
          stepSize: 10,
          autoSkip: false,
          font: { size: 8 },
          color: "#666666",
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
      datalabels: { display: false },
    },
    animation: false,
    elements: {
      line: { borderWidth: 1 },
    },
  };
  return (
    <Line
      data={dataFields}
      options={options}
      plugins={[dottedGridPlugin]}
    />
  );
};



const AudioGram = ({
  fields,
  setOscillaAudiogramState,
  oscillaAudiogramState
}) => {
  return (
    <>
      <Container maxWidth="xl">
        <Grid
          container
          // spacing={2}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          <Grid item xs={5} md={5.8}>
            <Box
              pb={5}
              sx={{
                backgroundColor: "#FFF6F6",
                maxHeight: "72vh",
                maxWidth: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "center",
                }}
              >
                <Typography color="error" variant="h6" ml={4}>
                  • Right
                </Typography>
              </Box>

              <ChartComponent
                rightEar
                optional={true}
                heardAC={[
                  oscillaAudiogramState?.ac_r_125,
                  oscillaAudiogramState?.ac_r_250,
                  oscillaAudiogramState?.ac_r_500,
                  oscillaAudiogramState?.ac_r_750,
                  oscillaAudiogramState?.ac_r_1000,
                  oscillaAudiogramState?.ac_r_1500,
                  oscillaAudiogramState?.ac_r_2000,
                  oscillaAudiogramState?.ac_r_3000,
                  oscillaAudiogramState?.ac_r_4000,
                  oscillaAudiogramState?.ac_r_6000,
                  oscillaAudiogramState?.ac_r_8000
                ]}
                heardBC={[
                  oscillaAudiogramState?.bc_r_125,
                  oscillaAudiogramState?.bc_r_250,
                  oscillaAudiogramState?.bc_r_500,
                  oscillaAudiogramState?.bc_r_750,
                  oscillaAudiogramState?.bc_r_1000,
                  oscillaAudiogramState?.bc_r_1500,
                  oscillaAudiogramState?.bc_r_2000,
                  oscillaAudiogramState?.bc_r_3000,
                  oscillaAudiogramState?.bc_r_4000,
                  oscillaAudiogramState?.bc_r_6000,
                  oscillaAudiogramState?.bc_r_8000
                ]}
                n_heardAC={[
                  oscillaAudiogramState?.ac_r_125_nh,
                  oscillaAudiogramState?.ac_r_250_nh,
                  oscillaAudiogramState?.ac_r_500_nh,
                  oscillaAudiogramState?.ac_r_750_nh,
                  oscillaAudiogramState?.ac_r_1000_nh,
                  oscillaAudiogramState?.ac_r_1500_nh,
                  oscillaAudiogramState?.ac_r_2000_nh,
                  oscillaAudiogramState?.ac_r_3000_nh,
                  oscillaAudiogramState?.ac_r_4000_nh,
                  oscillaAudiogramState?.ac_r_6000_nh,
                  oscillaAudiogramState?.ac_r_8000_nh
                ]}
                n_heardBC={[
                  oscillaAudiogramState?.bc_r_125_nh,
                  oscillaAudiogramState?.bc_r_250_nh,
                  oscillaAudiogramState?.bc_r_500_nh,
                  oscillaAudiogramState?.bc_r_750_nh,
                  oscillaAudiogramState?.bc_r_1000_nh,
                  oscillaAudiogramState?.bc_r_1500_nh,
                  oscillaAudiogramState?.bc_r_2000_nh,
                  oscillaAudiogramState?.bc_r_3000_nh,
                  oscillaAudiogramState?.bc_r_4000_nh,
                  oscillaAudiogramState?.bc_r_6000_nh,
                  oscillaAudiogramState?.bc_r_8000_nh
                ]}
              />
            </Box>
          </Grid>
          <Grid item xs={1} md={0.4}>
          </Grid>
          <Grid item xs={5} md={5.8}>
            <Box
              pb={5}
              sx={{
                backgroundColor: "#F6FAFF",
                maxWidth: "100%",
                maxHeight: "72vh",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "center",
                }}
              >
                <Typography color="primary" variant="h6" ml={4}>
                  • Left
                </Typography>
              </Box>
              <ChartComponent
                optional={true}
                heardAC={[
                  oscillaAudiogramState?.ac_l_125,
                  oscillaAudiogramState?.ac_l_250,
                  oscillaAudiogramState?.ac_l_500,
                  oscillaAudiogramState?.ac_l_750,
                  oscillaAudiogramState?.ac_l_1000,
                  oscillaAudiogramState?.ac_l_1500,
                  oscillaAudiogramState?.ac_l_2000,
                  oscillaAudiogramState?.ac_l_3000,
                  oscillaAudiogramState?.ac_l_4000,
                  oscillaAudiogramState?.ac_l_6000,
                  oscillaAudiogramState?.ac_l_8000
                ]}
                heardBC={[
                  oscillaAudiogramState?.bc_l_125,
                  oscillaAudiogramState?.bc_l_250,
                  oscillaAudiogramState?.bc_l_500,
                  oscillaAudiogramState?.bc_l_750,
                  oscillaAudiogramState?.bc_l_1000,
                  oscillaAudiogramState?.bc_l_1500,
                  oscillaAudiogramState?.bc_l_2000,
                  oscillaAudiogramState?.bc_l_3000,
                  oscillaAudiogramState?.bc_l_4000,
                  oscillaAudiogramState?.bc_l_6000,
                  oscillaAudiogramState?.bc_l_8000
                ]}
                n_heardAC={[
                  oscillaAudiogramState?.ac_l_125_nh,
                  oscillaAudiogramState?.ac_l_250_nh,
                  oscillaAudiogramState?.ac_l_500_nh,
                  oscillaAudiogramState?.ac_l_750_nh,
                  oscillaAudiogramState?.ac_l_1000_nh,
                  oscillaAudiogramState?.ac_l_1500_nh,
                  oscillaAudiogramState?.ac_l_2000_nh,
                  oscillaAudiogramState?.ac_l_3000_nh,
                  oscillaAudiogramState?.ac_l_4000_nh,
                  oscillaAudiogramState?.ac_l_6000_nh,
                  oscillaAudiogramState?.ac_l_8000_nh
                ]}
                n_heardBC={[
                  oscillaAudiogramState?.bc_l_125_nh,
                  oscillaAudiogramState?.bc_l_250_nh,
                  oscillaAudiogramState?.bc_l_500_nh,
                  oscillaAudiogramState?.bc_l_750_nh,
                  oscillaAudiogramState?.bc_l_1000_nh,
                  oscillaAudiogramState?.bc_l_1500_nh,
                  oscillaAudiogramState?.bc_l_2000_nh,
                  oscillaAudiogramState?.bc_l_3000_nh,
                  oscillaAudiogramState?.bc_l_4000_nh,
                  oscillaAudiogramState?.bc_l_6000_nh,
                  oscillaAudiogramState?.bc_l_8000_nh
                ]}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default memo(AudioGram);

{
  /* <Box
  sx={{
    position: 'fixed',
    bottom: 0,
    left: 0,
    width: '100%',
    backgroundColor: 'white',
    padding: '12px',
    display: 'flex',
    justifyContent: 'flex-end',
   
  }}
>
  <Button onClick={() => { setValue(value - 1) }} style={{ width: "10%" }} variant="contained" color="primary">
    <Typography style={{ fontFamily: "league spartan" }}>Save</Typography>
  </Button>
</Box> */
}
